import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { workforceApi } from "./workforceApi";

/**
 * WF-102 · Work Groups Tab
 *
 * Product Owner directive 2026-02-04 — Work Groups become a managed
 * business entity. Card grid + CRUD + archive. Referential integrity is
 * enforced by the backend (archive refused while employees still assigned).
 */
export default function WorkGroupsTab({ readOnly, onReload }) {
  const [rows, setRows] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: rpt }] = await Promise.all([
        workforceApi.workGroups(showArchived),
        workforceApi.workGroupMigrationReport(),
      ]);
      setRows(data);
      setReport(rpt);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Work Groups");
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const archive = async (row) => {
    if (!window.confirm(`Archive Work Group "${row.name}"?`)) return;
    try {
      await workforceApi.archiveWorkGroup(row.code);
      toast.success(`${row.name} archived`);
      load();
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };

  const unarchive = async (row) => {
    try {
      await workforceApi.unarchiveWorkGroup(row.code);
      toast.success(`${row.name} restored`);
      load();
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <div data-testid="workforce-work-groups-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Work Groups</h2>
          <p className="text-sm text-slate-500">
            Coolman organises its workforce into managed groups. Each group is a business entity
            employees are assigned to.
          </p>
        </div>
        {!readOnly && (
          <button
            data-testid="wf-work-group-new"
            onClick={() => setDlg({ open: true, editing: null })}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900"
          >
            <Plus size={13} weight="bold" /> New Work Group
          </button>
        )}
      </div>

      <label className="mb-4 inline-flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          data-testid="wf-work-groups-show-archived"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        Show archived groups
      </label>

      {loading ? (
        <div className="p-6 text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((wg) => (
            <div
              key={wg.code}
              data-testid={`wf-work-group-card-${wg.code}`}
              className={`rounded-lg border bg-white p-4 shadow-sm ${
                wg.active ? "border-slate-200" : "border-dashed border-slate-300 opacity-70"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-display text-base font-semibold text-slate-900">{wg.name}</h3>
                  {wg.description && (
                    <p className="mt-1 text-xs text-slate-500">{wg.description}</p>
                  )}
                </div>
                {!wg.active && (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                    Archived
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span
                  data-testid={`wf-work-group-count-${wg.code}`}
                  className="font-mono text-2xl font-semibold text-slate-900"
                >
                  {wg.employee_count}
                </span>
                <span className="text-xs text-slate-500">
                  {wg.employee_count === 1 ? "active employee" : "active employees"}
                </span>
              </div>
              {!readOnly && (
                <div className="mt-3 flex gap-2">
                  <button
                    data-testid={`wf-work-group-edit-${wg.code}`}
                    onClick={() => setDlg({ open: true, editing: wg })}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <PencilSimple size={11} /> Edit
                  </button>
                  {wg.active ? (
                    <button
                      data-testid={`wf-work-group-archive-${wg.code}`}
                      onClick={() => archive(wg)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <Archive size={11} /> Archive
                    </button>
                  ) : (
                    <button
                      data-testid={`wf-work-group-unarchive-${wg.code}`}
                      onClick={() => unarchive(wg)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <ArrowCounterClockwise size={11} /> Restore
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {report?.history?.length > 0 && (
        <details className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="wf-work-group-migration-report">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">
            Reconciliation Report ({report.history.length} run{report.history.length === 1 ? "" : "s"})
          </summary>
          <div className="mt-3 space-y-3">
            {report.history.map((run) => (
              <div key={run.id} className="rounded-md border border-slate-200 bg-white p-3 text-xs">
                <div className="mb-2 text-[11px] text-slate-500">
                  Run {new Date(run.run_at).toLocaleString("en-CA")} · remapped {run.remapped_count} · canonical {run.skipped_canonical_count} · unmapped {run.unmapped_count}
                </div>
                {run.remapped.length > 0 && (
                  <table className="w-full text-left">
                    <thead className="text-[10px] uppercase text-slate-500">
                      <tr><th className="py-1">Employee</th><th>From</th><th>To</th></tr>
                    </thead>
                    <tbody>
                      {run.remapped.map((r) => (
                        <tr key={r.employee_id} className="border-t border-slate-100">
                          <td className="py-1 font-medium text-slate-900">{r.full_name}</td>
                          <td className="text-slate-600">{r.from}</td>
                          <td className="text-slate-900">→ {r.to}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {run.unmapped.length > 0 && (
                  <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                    <div className="font-semibold">Unmapped free-text values:</div>
                    <ul className="ml-4 list-disc">
                      {run.unmapped.map((u, i) => (
                        <li key={i}>{u.full_name}: {u.from}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {dlg.open && (
        <WorkGroupDialog
          editing={dlg.editing}
          onClose={() => setDlg({ open: false, editing: null })}
          onSaved={() => { setDlg({ open: false, editing: null }); load(); onReload?.(); }}
        />
      )}
    </div>
  );
}

function WorkGroupDialog({ editing, onClose, onSaved }) {
  const [name, setName] = useState(editing?.name || "");
  const [description, setDescription] = useState(editing?.description || "");
  const [active, setActive] = useState(editing?.active ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await workforceApi.updateWorkGroup(editing.code, { name, description, active });
        toast.success("Work Group updated");
      } else {
        await workforceApi.createWorkGroup({ name, description, active });
        toast.success("Work Group created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="wf-work-group-dialog">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h3 className="mb-4 font-display text-lg font-semibold text-slate-900">
          {editing ? "Edit Work Group" : "New Work Group"}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700">Name</label>
            <input
              data-testid="wf-work-group-input-name"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Administration"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700">Description</label>
            <textarea
              data-testid="wf-work-group-input-description"
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this group does…"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="wf-work-group-input-active"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active (available for assignment)
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            data-testid="wf-work-group-cancel"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="wf-work-group-save"
            onClick={save}
            disabled={saving}
            className="rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
